import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { GET_USER_PROFILE_ROLES_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { UserProfileRolesResponse, UserProfileWithRoles } from '../../integrations/allkort/graphql/interface/Customer';

const ROLES_PER_PAGE = 5;
const MAX_PROFILES = 500;

/**
 * Hook for fetching ALL user profile roles under currentCustomer with automatic pagination.
 * Fetches 5 at a time and follows hasNextPage until all pages are loaded.
 * Uses network-only since roles change after mutations.
 *
 * Returns UserProfileWithRoles[] — join to UserProfile by id in the consuming component.
 *
 * Usage:
 * const { profileRoles, loading, error, isComplete } = useGetAllUserProfileRoles();
 */
export const useGetAllUserProfileRoles = () => {
	const [allProfileRoles, setAllProfileRoles] = useState<UserProfileWithRoles[]>([]);
	const [currentCursor, setCurrentCursor] = useState<string | null>(null);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [isComplete, setIsComplete] = useState(false);

	const variables = useMemo(() => ({
		first: ROLES_PER_PAGE,
		after: currentCursor,
		last: null,
		before: null,
	}), [currentCursor]);

	const { data, loading, error } = useQuery<UserProfileRolesResponse>(
		GET_USER_PROFILE_ROLES_QUERY,
		{
			variables,
			fetchPolicy: 'network-only',
			skip: isComplete,
		}
	);

	useEffect(() => {
		if (loading) return; // skip while new query is in-flight — data may be stale from previous cursor
		if (!data?.currentCustomer?.userProfiles) return;

		const { nodes, pageInfo } = data.currentCustomer.userProfiles;
		if (nodes && nodes.length > 0) {
			setAllProfileRoles(prev => {
				if (!currentCursor) {
					return nodes;
				}
				const existingIds = new Set(prev.map(p => p.id));
				const newEntries = nodes.filter(p => !existingIds.has(p.id));
				return [...prev, ...newEntries];
			});

			const totalLoaded = currentCursor
				? allProfileRoles.length + nodes.length
				: nodes.length;

			if (pageInfo.hasNextPage && totalLoaded < MAX_PROFILES) {
				setCurrentCursor(pageInfo.endCursor);
				setIsFetchingMore(true);
			} else {
				setIsFetchingMore(false);
				setIsComplete(true);
			}
		} else {
			setIsFetchingMore(false);
			setIsComplete(true);
		}
	}, [data, loading, currentCursor]);

	const refetch = () => {
		setAllProfileRoles([]);
		setCurrentCursor(null);
		setIsFetchingMore(false);
		setIsComplete(false);
	};

	return {
		profileRoles: allProfileRoles,
		loading: loading || isFetchingMore,
		error,
		isComplete,
		refetch,
	};
};
